import { ChevronV2 } from 'assets/icons/ChevronV2'
import { Input } from 'components/Form'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { BodyText } from 'styling/Typography'
import { SelectOption, SelectOptionsContainer } from './Select.styles'
import { Option } from './Select'
import { useQuery } from '@tanstack/react-query'

export const AsyncSelect = <T,>({
  nullValue,
  loadOptions,
  decoder,
  value,
  onChange,
  label,
  placeholder,
  width,
  error,
  defaultSelectFirstOption = false,
  optionKey,
}: {
  nullValue?: Option
  loadOptions: (search: string) => Promise<T[]>
  decoder: (item: T) => Option
  value?: Option | null
  onChange: (value: Option) => void
  label?: string
  placeholder?: string
  width?: string
  error?: string
  defaultSelectFirstOption?: boolean
  optionKey: string
}) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { data } = useQuery({
    queryKey: ['async-select-options-' + optionKey, inputValue],
    queryFn: () => loadOptions(inputValue || undefined),
  })
  const options = data?.map(decoder) || []
  const inputRef = useRef<HTMLInputElement>(null)

  const calculatePosition = () => {
    const rect = inputRef.current?.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth
    const containerHeight = 200
    const containerWidth = 200
    if (!rect)
      return {
        top: 0,
        left: 0,
      }
    const position = {
      top: rect.top + 50,
      left: rect.left,
    }

    if (position.top + containerHeight > windowHeight) {
      return {
        bottom: windowHeight - rect.top + 10,
        left: rect.left,
      }
    }
    if (position.left + containerWidth > windowWidth) {
      position.left = rect.left - containerWidth
    }
    return {
      top: position.top,
      left: position.left,
    }
  }

  useEffect(() => {
    inputRef.current?.addEventListener('focus', () => {
      setOpen(true)
    })
    inputRef.current?.addEventListener('blur', () => {
      setTimeout(() => {
        setOpen(false)
      }, 100)
    })
  }, [])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setInputValue('')
        inputRef?.current?.blur()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  useEffect(() => {
    if (defaultSelectFirstOption && options.length > 0 && !value) {
      onChange(options[0])
    }
  }, [defaultSelectFirstOption, options])

  return (
    <Input
      width={width}
      label={label}
      placeholder={placeholder}
      ref={inputRef}
      value={value?.label || inputValue}
      onChange={(e) => {
        setInputValue(e.target.value)
      }}
      error={error}
    >
      <ChevronIconContainer>
        <ChevronV2 open={open} />
      </ChevronIconContainer>

      {open &&
        createPortal(
          <SelectOptionsContainer
            style={{
              ...calculatePosition(),
            }}
            ref={dropdownRef}
          >
            {nullValue && (
              <SelectOption
                active={!value?.value}
                onMouseDown={() => {
                  onChange(nullValue)
                  setInputValue('')
                  setOpen(false)
                  inputRef?.current?.blur()
                }}
              >
                {nullValue.label}
              </SelectOption>
            )}
            {loading ? (
              <SelectOption>
                <BodyText>Loading...</BodyText>
              </SelectOption>
            ) : (
              options.map((option) => (
                <SelectOption
                  key={option.value}
                  active={option.value.toString() === value?.value?.toString()}
                  onMouseDown={() => {
                    onChange(option)
                    setInputValue('')
                    setOpen(false)
                    inputRef?.current?.blur()
                  }}
                >
                  {option.label}
                </SelectOption>
              ))
            )}
          </SelectOptionsContainer>,
          document.body,
        )}
    </Input>
  )
}

const ChevronIconContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  height: 1.5rem;
  color: ${(props) => props.theme.textColor};
`
